import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  FileText, Search, Trash2, Loader2, Download, Eye, Plus,
  Calendar, Building2, CreditCard, Filter, ArrowUpDown, IndianRupee,
  CheckCircle2, Clock, AlertCircle, ChevronRight, CheckSquare, Square, MoreHorizontal,
  PenTool, ShieldCheck
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { usePlan } from "../context/PlanContext";
import EmptyState from "../components/EmptyState";
import { formatIndianDate } from "../utils/format";
import { UpgradeNudge } from "../components/FeatureGate";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  unpaid: { bg: "bg-red-50", text: "text-red-700", label: "Unpaid" },
  partial: { bg: "bg-amber-50", text: "text-amber-700", label: "Partial" },
  paid: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Paid" },
};

export default function InvoicesPage() {
  const { isAuthenticated, loading: authLoading, login } = useAuth();
  const { isFree, invoiceHistoryLimit } = usePlan();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = searchParams.get("type") || "all";
  const [invoices, setInvoices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState(initialType);
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [gstFilter, setGstFilter] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchData();
    } else if (!authLoading) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    try {
      const [invRes, compRes] = await Promise.all([
        axios.get(`${API}/builder/invoices/list`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
      ]);
      setInvoices(invRes.data.invoices || []);
      setCompanies(compRes.data.companies || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm("Delete this invoice? The invoice number stays reserved (cannot be re-issued) and the entry will remain visible with a 'Deleted' marker for audit.")) return;
    setDeleting(invoiceId);
    try {
      await axios.delete(`${API}/builder/invoices/detail/${invoiceId}`, { withCredentials: true });
      // Mark as deleted locally so the row remains visible with the badge
      setInvoices(prev => prev.map(i => i.invoice_id === invoiceId ? { ...i, deleted: true, status: "deleted", deleted_at: new Date().toISOString() } : i));
      toast.success("Invoice deleted — number stays reserved for audit");
    } catch (err) {
      toast.error("Failed to delete invoice");
    } finally {
      setDeleting(null);
    }
  };

  const filtered = useMemo(() => {
    let result = [...invoices];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(i =>
        i.customer_name?.toLowerCase().includes(q) ||
        i.invoice_number?.toLowerCase().includes(q)
      );
    }
    if (companyFilter !== "all") result = result.filter(i => i.company_id === companyFilter);
    if (typeFilter !== "all") result = result.filter(i => i.invoice_type === typeFilter);
    if (paymentFilter !== "all") result = result.filter(i => i.payment_status === paymentFilter);
    if (gstFilter.trim()) {
      const g = gstFilter.trim().toUpperCase();
      result = result.filter(i => (i.customer_gst || "").toUpperCase().includes(g));
    }
    if (fromDate) {
      result = result.filter(i => (i.invoice_date || "") >= fromDate);
    }
    if (toDate) {
      result = result.filter(i => (i.invoice_date || "") <= toDate);
    }

    result.sort((a, b) => {
      if (sortBy === "grand_total") return (b.grand_total || 0) - (a.grand_total || 0);
      return (b[sortBy] || "").localeCompare(a[sortBy] || "");
    });
    return result;
  }, [invoices, search, companyFilter, typeFilter, paymentFilter, sortBy, fromDate, toDate, gstFilter]);

  const activeFilterCount = [
    companyFilter !== "all",
    paymentFilter !== "all",
    !!fromDate,
    !!toDate,
    !!gstFilter.trim(),
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setCompanyFilter("all");
    setPaymentFilter("all");
    setFromDate("");
    setToDate("");
    setGstFilter("");
    setSearch("");
  };

  const companyMap = Object.fromEntries(companies.map(c => [c.company_id, c.company_name]));

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(i => i.invoice_id)));
    }
  };

  const handleBulkAction = async (action) => {
    if (selected.size === 0) { toast.error("Select invoices first"); return; }
    const label = action === "mark_paid" ? "mark as paid" : "delete";
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selected.size} invoice(s)?`)) return;
    setBulkLoading(true);
    try {
      await axios.post(`${API}/builder/invoices/bulk-action`, {
        invoice_ids: Array.from(selected),
        action,
      }, { withCredentials: true });
      toast.success(`Bulk ${label} successful`);
      setSelected(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkSign = async () => {
    if (selected.size === 0) { toast.error("Select invoices first"); return; }
    if (!window.confirm(`Digitally sign ${selected.size} invoice(s)?`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/builder/invoices/bulk-sign`, {
        invoice_ids: Array.from(selected),
      }, { withCredentials: true });
      toast.success(`${res.data.signed} invoice(s) signed, ${res.data.skipped} skipped`);
      setSelected(new Set());
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk sign failed");
    } finally {
      setBulkLoading(false);
    }
  };

  const formatCurrency = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

  if (!authLoading && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <FileText className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Sign in to view invoices</h2>
          <p className="text-slate-500 mb-6">Your invoice history is secured behind authentication.</p>
          <Button onClick={login} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-prompt-btn">Sign in with Google</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="invoices-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Invoices</h1>
            <p className="text-sm text-slate-500">{filtered.length} invoice{filtered.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate("/upload")} variant="outline" className="text-sm" data-testid="upload-invoice-btn">
              <Search className="w-4 h-4 mr-1" /> Analyze Invoice
            </Button>
            <Button onClick={() => navigate("/create")} className="bg-blue-600 hover:bg-blue-700 text-white text-sm" data-testid="create-invoice-btn">
              <Plus className="w-4 h-4 mr-1" /> Create Invoice
            </Button>
          </div>
        </div>

        {/* Type Tabs */}
        <div className="flex items-center gap-1 mb-3 bg-slate-100 rounded-xl p-1 w-full sm:w-fit overflow-x-auto whitespace-nowrap" data-testid="type-tabs">
          {[
            { id: "all", label: "All" },
            { id: "sales", label: "Sales" },
            { id: "purchase", label: "Purchase" },
            { id: "credit_note", label: "Credit Notes" },
            { id: "quotation", label: "Quotations" },
          ].map(tab => {
            const count = tab.id === "all"
              ? invoices.length
              : invoices.filter(i => i.invoice_type === tab.id).length;
            const isActive = typeFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setTypeFilter(tab.id);
                  const next = new URLSearchParams(searchParams);
                  if (tab.id === "all") next.delete("type"); else next.set("type", tab.id);
                  setSearchParams(next, { replace: true });
                }}
                className={`px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                  isActive ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full ${
                    isActive ? "bg-blue-100 text-blue-700" : "bg-slate-200 text-slate-600"
                  }`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search invoices..."
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="filter-company"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={paymentFilter} onValueChange={setPaymentFilter}>
              <SelectTrigger className="w-full sm:w-36" data-testid="filter-payment"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(v => !v)}
              className="text-sm flex-shrink-0"
              data-testid="toggle-advanced-filters"
            >
              <Filter className="w-4 h-4 mr-1" />
              More filters
              {activeFilterCount > 0 && (
                <span
                  className="ml-1.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded-full px-1.5 py-0.5"
                  data-testid="active-filter-count"
                >
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
          {showAdvancedFilters && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3 bg-slate-50 border border-slate-100 rounded-xl p-3" data-testid="advanced-filters-panel">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wider">Client GSTIN</label>
                <Input
                  value={gstFilter}
                  onChange={e => setGstFilter(e.target.value.toUpperCase())}
                  placeholder="29AABCT1234A1Z5"
                  className="text-sm font-mono uppercase"
                  data-testid="filter-gstin"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wider">From date</label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="text-sm"
                  data-testid="filter-from-date"
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1 uppercase tracking-wider">To date</label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="text-sm"
                  data-testid="filter-to-date"
                />
              </div>
              {activeFilterCount > 0 && (
                <div className="sm:col-span-3 flex justify-end">
                  <button
                    onClick={clearAllFilters}
                    className="text-xs font-semibold text-slate-500 hover:text-slate-800"
                    data-testid="clear-filters"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bulk Action Bar */}
        {selected.size > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-4 flex items-center gap-3" data-testid="bulk-action-bar">
            <span className="text-sm font-medium text-blue-800">{selected.size} selected</span>
            <div className="flex items-center gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={handleBulkSign} disabled={bulkLoading} className="text-xs border-violet-200 text-violet-700 hover:bg-violet-50" data-testid="bulk-sign">
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <PenTool className="w-3 h-3 mr-1" />} Sign
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("mark_paid")} disabled={bulkLoading} className="text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="bulk-mark-paid">
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />} Mark Paid
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("delete")} disabled={bulkLoading} className="text-xs border-red-200 text-red-600 hover:bg-red-50" data-testid="bulk-delete">
                {bulkLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />} Delete
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} className="text-xs text-slate-500" data-testid="bulk-clear">Clear</Button>
            </div>
          </div>
        )}

        {/* Invoice List */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FileText}
            iconBg="bg-blue-50 text-blue-600"
            title="No invoices yet"
            description="Create your first GST-compliant invoice in under 60 seconds — or pull one in by uploading a PDF / image."
            bullets={[
              "Auto-fill customer & product details from your masters",
              "Branded PDF + share link sent to customer instantly",
              "Track payments, mark as paid, issue Credit Notes when needed",
            ]}
            primaryAction={{ label: "Create Invoice", to: "/create", icon: Plus }}
            secondaryAction={{ label: "Upload existing invoice", to: "/upload" }}
            testId="invoices-empty-state"
          />
        ) : (
          <div className="space-y-2">
            {/* History Limit Banner */}
            {isFree && invoiceHistoryLimit > 0 && filtered.length > invoiceHistoryLimit && (
              <UpgradeNudge feature="full_history" message={`Only last ${invoiceHistoryLimit} invoices are accessible. Upgrade to view full history.`} />
            )}
            {/* Select All */}
            <div className="flex items-center gap-2 px-2 py-1">
              <button onClick={toggleAll} className="text-slate-400 hover:text-blue-600 transition-colors" data-testid="select-all-btn">
                {selected.size === filtered.length && filtered.length > 0 ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
              </button>
              <span className="text-xs text-slate-400">Select all</span>
            </div>
            {filtered.map((inv, idx) => {
              const status = STATUS_STYLES[inv.payment_status] || STATUS_STYLES.unpaid;
              const isLocked = isFree && invoiceHistoryLimit > 0 && idx >= invoiceHistoryLimit;
              const isDeleted = !!inv.deleted;
              return (
                <motion.div
                  key={inv.invoice_id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-white rounded-xl border shadow-sm p-4 transition-all ${
                    isLocked
                      ? "opacity-50 pointer-events-none select-none relative border-slate-100"
                      : isDeleted
                        ? "border-red-100 bg-red-50/30 hover:shadow-md cursor-pointer group"
                        : "border-slate-100 hover:shadow-md cursor-pointer group"
                  }`}
                  onClick={() => !isLocked && navigate(`/invoices/${inv.invoice_id}`)}
                  data-testid={`invoice-row-${inv.invoice_id}`}
                >
                  {isLocked && (
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px] rounded-xl z-10 flex items-center justify-center" style={{ filter: "none" }}>
                      <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-3 py-1 rounded-full border border-violet-200">Upgrade to access</span>
                    </div>
                  )}
                  <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSelect(inv.invoice_id); }}
                      className="text-slate-300 hover:text-blue-600 transition-colors flex-shrink-0 mt-1 sm:mt-0"
                      data-testid={`select-${inv.invoice_id}`}
                    >
                      {selected.has(inv.invoice_id) ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                    </button>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      inv.invoice_type === "sales" ? "bg-blue-50 text-blue-600" : "bg-purple-50 text-purple-600"
                    }`}>
                      <FileText className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-0.5">
                        <span className={`font-semibold text-sm truncate ${isDeleted ? "text-slate-500 line-through" : "text-slate-900"}`}>{inv.customer_name}</span>
                        <span className={`text-[11px] font-mono ${isDeleted ? "text-slate-400 line-through" : "text-slate-400"}`}>{inv.invoice_number}</span>
                        {isDeleted && (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-100 border border-red-200 px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                            title={inv.deleted_at ? `Deleted on ${formatIndianDate(inv.deleted_at)}` : "Deleted invoice"}
                            data-testid={`deleted-badge-${inv.invoice_id}`}
                          >
                            <Trash2 className="w-2.5 h-2.5" /> Deleted
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                        <span className="truncate max-w-[140px]">{companyMap[inv.company_id] || "—"}</span>
                        <span>{formatIndianDate(inv.invoice_date)}</span>
                        <span className="capitalize">{inv.invoice_type}</span>
                      </div>
                      {/* Mobile-only: status + amount inline below */}
                      <div className="flex sm:hidden items-center justify-between gap-2 mt-2">
                        <div className="flex items-center gap-1.5">
                          {inv.digital_signature && (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                              <ShieldCheck className="w-2.5 h-2.5" /> Signed
                            </span>
                          )}
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>{status.label}</span>
                        </div>
                        <span className="text-sm font-bold text-slate-900">{formatCurrency(inv.grand_total)}</span>
                      </div>
                    </div>

                    {/* Desktop right-aligned column */}
                    <div className="hidden sm:flex items-center gap-2">
                      {inv.digital_signature && (
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full flex items-center gap-0.5" data-testid={`signed-badge-${inv.invoice_id}`}>
                          <ShieldCheck className="w-2.5 h-2.5" /> Signed
                        </span>
                      )}
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                      <span className="text-base font-bold text-slate-900 min-w-[80px] text-right">
                        {formatCurrency(inv.grand_total)}
                      </span>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await axios.get(`${API}/builder/invoices/${inv.invoice_id}/pdf`, {
                              withCredentials: true,
                              responseType: "blob",
                            });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = `${(inv.invoice_number || "invoice").replace(/\//g, "-")}.pdf`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                          } catch {
                            toast.error("Failed to download PDF");
                          }
                        }}
                        className="p-1.5 text-slate-300 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`pdf-${inv.invoice_id}`}
                        title="Download PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); if (!isDeleted) handleDelete(inv.invoice_id); }}
                        disabled={isDeleted}
                        className={`p-1.5 transition-opacity ${isDeleted ? "text-slate-200 cursor-not-allowed opacity-40" : "text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100"}`}
                        data-testid={`delete-${inv.invoice_id}`}
                        title={isDeleted ? "Already deleted" : "Delete invoice"}
                      >
                        {deleting === inv.invoice_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors" />
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
